import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getUserByEmail, debugCurrentUser } from "../lib/api/users";

interface ProfileCheckProps {
  children: React.ReactNode;
}

const ProfileCheck: React.FC<ProfileCheckProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authStatus } = useAuthenticator();
  const [isChecking, setIsChecking] = useState(true);
  const [profileCompleted, setProfileCompleted] = useState(false);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      console.log("ProfileCheck: authStatus =", authStatus);
      console.log("ProfileCheck: user =", user);

      // Check for stored tokens as fallback
      const hasStoredTokens =
        sessionStorage.getItem("access_token") &&
        sessionStorage.getItem("id_token");
      console.log("ProfileCheck: hasStoredTokens =", hasStoredTokens);

      if ((authStatus === "authenticated" && user) || hasStoredTokens) {
        try {
          console.log(
            "Checking profile completion for user:",
            user?.userId || "No user ID"
          );

          // Debug: Show current user info
          debugCurrentUser();

          // Get user email from Cognito or stored tokens
          let userEmail = user?.signInDetails?.loginId;

          // If no email from Cognito but we have stored tokens, try to decode the ID token
          if (!userEmail && hasStoredTokens) {
            try {
              const idToken = sessionStorage.getItem("id_token");
              if (idToken) {
                const payload = JSON.parse(atob(idToken.split(".")[1]));
                userEmail = payload.email;
                // Store email in sessionStorage for other components to use
                if (userEmail) {
                  sessionStorage.setItem("email", userEmail);
                  console.log("User email from stored token:", userEmail);
                }
              }
            } catch (error) {
              console.error("Error decoding stored token:", error);
            }
          }

          console.log("User email from Cognito:", userEmail);

          if (!userEmail) {
            console.log("No email found, redirecting to profile setup");
            setProfileCompleted(false);
            setIsChecking(false);
            navigate("/profile-setup", { replace: true });
            return;
          }

          // Try to get the current user from our API/DynamoDB
          const currentUser = await getUserByEmail(userEmail);
          console.log("Current user from API:", currentUser);

          if (currentUser) {
            console.log("User found:", currentUser);
            console.log(
              "Profile completed flag:",
              currentUser.profileCompleted
            );

            if (currentUser.profileCompleted) {
              // User exists and profile is completed
              console.log("Profile is completed, allowing access");
              setProfileCompleted(true);
            } else {
              // User exists but profile is not completed
              console.log("Profile not completed, redirecting to setup");
              setProfileCompleted(false);
              setIsChecking(false);
              console.log("About to navigate to /profile-setup");
              navigate("/profile-setup", { replace: true });
            }
          } else {
            // User doesn't exist - this means they're a new user
            console.log("New user, redirecting to profile setup");
            setProfileCompleted(false);
            setIsChecking(false);
            console.log("About to navigate to /profile-setup");
            navigate("/profile-setup", { replace: true });
          }
        } catch (error) {
          console.error("Error checking profile completion:", error);
          // On error, assume profile is not completed
          setProfileCompleted(false);
          setIsChecking(false);
          navigate("/profile-setup", { replace: true });
        } finally {
          setIsChecking(false);
        }
      } else if (authStatus === "unauthenticated") {
        setIsChecking(false);
      }
    };

    // Skip profile check if we're already on the profile setup page
    if (location.pathname === "/profile-setup") {
      setIsChecking(false);
      return;
    }

    // Only run if we're not already checking or if the auth state has actually changed
    if (isChecking) {
      checkProfileCompletion();
    }
  }, [user, authStatus, location.pathname]);

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Checking profile...</p>
        </div>
      </div>
    );
  }

  // If profile is completed, render children
  if (profileCompleted) {
    return <>{children}</>;
  }

  // If profile is not completed, this should be handled by the redirect above
  // But as a fallback, show loading
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-lg">Redirecting to profile setup...</p>
      </div>
    </div>
  );
};

export default ProfileCheck;
